import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { motion } from 'framer-motion';
import { Package } from 'lucide-react';

interface ProductPieChartProps {
  data: Array<{
    name: string;
    value: number;
    color?: string;
  }>;
  title?: string;
  height?: number;
}

const ChartContainer = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[6]};
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ChartTitle = styled.h3`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const PieSvg = styled.svg`
  width: 100%;
  height: 320px;
  max-width: 340px;
  display: block;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[4]};
  margin-top: ${props => props.theme.spacing[6]};
  justify-content: center;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
`;

const ColorDot = styled.div<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => props.$color};
  border: 2px solid ${props => props.theme.colors.border};
`;

const EmptyState = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${props => props.theme.spacing[8]};
  min-height: 400px;
`;

const EmptyIcon = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${props => props.theme.borders.radius.full};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}15 0%, ${props => props.theme.colors.primary}08 100%);
  border: 2px solid ${props => props.theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing[4]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 30%,
      ${props => props.theme.colors.primary}10 50%,
      transparent 70%
    );
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }

  svg {
    color: ${props => props.theme.colors.primary};
    position: relative;
    z-index: 1;
  }
`;

const EmptyTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing[2]} 0;
`;

const EmptyDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  max-width: 400px;
  line-height: 1.6;
`;

// defaultColors now derived at runtime inside component using useTheme

// getPieSegments agora será definido dentro do componente para acessar cores do tema

const ProductPieChart: React.FC<ProductPieChartProps> = ({ data, title, height = 320 }) => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  // cores padrão derivadas do tema atual
  const defaultColors = [
    theme.colors.primary,
    theme.colors.success,
    theme.colors.warning || '#f5a623',
    theme.colors.error,
    theme.colors.primary,
  ];

  function getPieSegments(localData: ProductPieChartProps['data']) {
    const total = localData.reduce((sum, d) => sum + d.value, 0);
    let startAngle = 0;
    if (localData.length === 1) {
      return [{
        ...localData[0],
        color: localData[0].color || defaultColors[0],
        startAngle: 0,
        endAngle: 360,
        percent: 100,
      }];
    }
    return localData.map((d, i) => {
      const angle = total ? (d.value / total) * 360 : 0;
      const endAngle = startAngle + angle;
      const segment = {
        ...d,
        color: d.color || defaultColors[i % defaultColors.length],
        startAngle,
        endAngle,
        percent: total ? (d.value / total) * 100 : 0,
      };
      startAngle = endAngle;
      return segment;
    });
  }

  const segments = getPieSegments(data || []);
  const radius = 120;
  const center = 160;

  function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    return [
      'M', start.x, start.y,
      'A', r, r, 0, largeArcFlag, 0, end.x, end.y,
      'L', cx, cy,
      'Z',
    ].join(' ');
  }

  function polarToCartesian(cx: number, cy: number, r: number, angle: number) {
    const rad = (angle - 90) * Math.PI / 180.0;
    return {
      x: cx + r * Math.cos(rad),
      y: cy + r * Math.sin(rad),
    };
  }

  // Se não houver produtos, mostrar placeholder padronizado
  if (!data || data.length === 0) {
    return (
      <ChartContainer>
        {title && <ChartTitle>{title}</ChartTitle>}
        <EmptyState
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <EmptyIcon>
            <Package size={56} />
          </EmptyIcon>
          <EmptyTitle>Nenhum produto vendido</EmptyTitle>
          <EmptyDescription>
            Os produtos vendidos aparecerão aqui quando houver registros de vendas.
          </EmptyDescription>
        </EmptyState>
      </ChartContainer>
    );
  }

  // Se houver produtos, exibe gráfico normal
  return (
    <ChartContainer>
      {title && <ChartTitle>{title}</ChartTitle>}
      <PieSvg width={height} height={height}>
        {segments.length === 1 ? (
          <motion.circle
            cx={center}
            cy={center}
            r={radius + 10}
            fill={segments[0].color}
            stroke={theme.colors.primary}
            strokeWidth={4}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            whileHover={{ scale: 1.04 }}
            style={{ cursor: 'pointer', filter: 'drop-shadow(0 0 8px ' + theme.colors.primary + ')' }}
            onMouseEnter={() => setActiveIndex(0)}
            onMouseLeave={() => setActiveIndex(null)}
          />
        ) : (
          segments.map((seg, i) => (
            <motion.path
              key={seg.name}
              d={describeArc(center, center, radius + (activeIndex === i ? 10 : 0), seg.startAngle, seg.endAngle)}
              fill={seg.color}
              stroke={activeIndex === i ? theme.colors.primary : theme.colors.border}
              strokeWidth={activeIndex === i ? 4 : 2}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              whileHover={{ scale: 1.04 }}
              style={{ cursor: 'pointer', filter: activeIndex === i ? 'drop-shadow(0 0 8px ' + theme.colors.primary + ')' : 'none' }}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
            />
          ))
        )}
        {/* Label central */}
        {activeIndex !== null && (
            <motion.text
            x={center}
            y={center - 10}
            textAnchor="middle"
              fill={theme.colors.textPrimary}
            fontSize={22}
            fontWeight={700}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            style={{ pointerEvents: 'none' }}
          >
            {segments[activeIndex].name}
          </motion.text>
        )}
        {activeIndex !== null && (
            <motion.text
            x={center}
            y={center + 18}
            textAnchor="middle"
              fill={theme.colors.textSecondary}
            fontSize={16}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            style={{ pointerEvents: 'none' }}
          >
            {`${segments[activeIndex].value} vendas (${segments[activeIndex].percent.toFixed(1)}%)`}
          </motion.text>
        )}
      </PieSvg>
      <LegendContainer>
        {segments.map((seg, i) => (
            <LegendItem key={seg.name}>
              <ColorDot $color={seg.color as string} />
              <span>{seg.name}</span>
              <span>{seg.value} vendas</span>
              <span>({seg.percent.toFixed(1)}%)</span>
            </LegendItem>
        ))}
      </LegendContainer>
    </ChartContainer>
  );
};

export default ProductPieChart;
